import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calendar, Copy, Send, Users, Clock, BarChart3, ChevronDown, Zap, LayoutTemplate } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LandingHeader } from '../../components/landing/LandingHeader';
import { CTASection } from '../../components/landing/CTASection';

export default function EmployeeScheduling() {
  useEffect(() => {
    document.title = 'Employee Scheduling Software | ReStaff';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Create employee schedules, manage shifts, and optimize workforce planning with ReStaff.';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="employee-scheduling-page">
      <LandingHeader />

      {/* HERO */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
            Employee Scheduling Software
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Create staff schedules in minutes, manage shifts across locations, and publish instantly — so your team always knows when they work.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8" data-testid="scheduling-hero-cta">
                Start Free Trial <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-sm text-slate-400">30-day free trial &middot; Cancel anytime, no questions asked</p>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5" style={{ fontFamily: 'Manrope' }}>
            What is Employee Scheduling Software?
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Employee scheduling software replaces spreadsheets and paper schedules with a digital system that lets managers create, publish, and manage staff shifts from one platform. ReStaff makes scheduling fast, visual, and conflict-free — whether you manage one location or twenty.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Build Your Schedule', desc: 'Drag and drop shifts onto a weekly grid. Assign employees by role, set start and end times, and add breaks.', icon: Calendar },
              { step: '2', title: 'Publish to Your Team', desc: 'Hit publish and your team sees their schedule instantly. Staff get notified and can view shifts from their phone.', icon: Send },
              { step: '3', title: 'Track & Optimize', desc: 'Monitor labor costs in real time. Use templates to repeat successful weeks and adjust based on demand.', icon: BarChart3 },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="bg-white border border-slate-100 rounded-xl p-6 text-center" data-testid={`scheduling-step-${s.step}`}>
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Scheduling Features
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {[
              { title: 'Drag-and-Drop Shift Builder', desc: 'Create shifts visually on a weekly grid. Drag to adjust times, drop employees onto shifts, and see the full picture at a glance.', icon: Calendar },
              { title: 'Copy Last Week', desc: 'Duplicate a previous week\'s schedule in one click. Make adjustments as needed instead of building from scratch every week.', icon: Copy },
              { title: 'Bulk Shift Creation', desc: 'Add multiple shifts across days and roles at once. Ideal for filling a full week or creating recurring patterns quickly.', icon: Zap },
              { title: 'Publish & Draft States', desc: 'Keep schedules in draft while planning. Publish when ready — employees only see the final, approved version.', icon: Send },
              { title: 'Weekly Templates', desc: 'Save your best week as a reusable template. Load it into any future week to skip repetitive scheduling work.', icon: LayoutTemplate },
              { title: 'Multi-Location Scheduling', desc: 'Each location gets its own schedule grid. Switch between branches or view all locations from one dashboard.', icon: Users },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 hover:border-orange-200 hover:shadow-sm transition-all" data-testid={`scheduling-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Why Use ReStaff for Scheduling
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Build a full week of shifts in minutes, not hours',
              'Reduce overtime by seeing labor costs as you schedule',
              'Eliminate scheduling conflicts with built-in detection',
              'Empower staff to view shifts and swap from their phone',
              'Reuse templates to save time every week',
              'Manage multiple locations from one account',
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-4">
                <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{b}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-500 text-center leading-relaxed">
            Pair scheduling with ReStaff <Link to="/time-tracking" className="text-orange-500 hover:underline font-medium">time tracking software</Link>, <Link to="/timesheet-software" className="text-orange-500 hover:underline font-medium">timesheet software</Link>, <Link to="/labor-cost-management" className="text-orange-500 hover:underline font-medium">labor cost management</Link>, and <Link to="/task-management" className="text-orange-500 hover:underline font-medium">task management software</Link> for a complete workforce solution. Built for <Link to="/restaurants" className="text-orange-500 hover:underline font-medium">restaurants</Link>, <Link to="/retail" className="text-orange-500 hover:underline font-medium">retail stores</Link>, and more.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: 'What is employee scheduling software?', a: 'Employee scheduling software is a digital tool that helps managers create, manage, and publish work schedules for their team. It replaces paper schedules and spreadsheets with a centralized system that staff can access from any device.' },
              { q: 'Can I schedule employees across multiple locations?', a: 'Yes. ReStaff supports multi-location scheduling. Each location has its own schedule grid, and managers can switch between them or manage all branches from a single dashboard.' },
              { q: 'How do employees see their schedule?', a: 'Once a schedule is published, employees can view their upcoming shifts from the ReStaff app on their phone or any browser. They receive notifications when new schedules are posted.' },
              { q: 'Can I copy a schedule from a previous week?', a: 'Yes. Use the "Copy Last Week" feature to duplicate a previous week\'s schedule. You can also save any week as a reusable template and load it into future weeks.' },
            ].map((item, i) => (
              <details key={i} className="group bg-slate-50 border border-slate-100 rounded-xl" data-testid={`scheduling-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-semibold text-slate-900 select-none">
                  {item.q}
                  <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-3" />
                </summary>
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
